/**
Author - Shadab
Git - https://github.com/m-shadab01
**/

import React from "react";
import SafeLink from "components/commons/SafeLink";
import styled from "styled-components";
import { Spinner } from "components";
import { CountryDropdown } from "components/NewPartners/commons";
import { WealthyEnv } from "helpers";

const downArrow = `${WealthyEnv.HOMEPAGE_CDN_URL}/partners-dropdown-arrow.svg`;
const infoIcon = `${WealthyEnv.CDN_URL}/articles/info-icon-grey.svg`;
const partnersPrivacyUrl = `${WealthyEnv.URL}/partners-privacy`;

const BlogForm = ({
  resendOtp,
  isLoading,
  isPartnerExisting,
  submitForm,
  formData,
  handlePaste,
  onChangeHandler,
  step,
  errorText,
  onKeyUpHandler,
  counter,
  selectedCountryCode,
  showDropdown,
  setShowDropdown,
  handleCountryCodeChange,
  hasReferralCode,
  handleReferralCheckbox,
  handleReferralValidation,
  referralStatus,
  onOtpChangeHandler
}) => {
  const numberOfOtpDigits = [...Array(5).keys()];

  const getReferralErrorMessage = () => {
    switch (referralStatus) {
      case "invalid":
        return formData.referralCode ? "Invalid referral code. Please try again." : "Please enter a referral code";
      case "valid":
        return "Referral code validated successfully";
      default:
        return "";
    }
  };

  const otpContent = numberOfOtpDigits.map(index => (
    <OtpInput
      autoFocus={index === 0}
      className="otp-input"
      data-index={index}
      id={index}
      key={index}
      maxLength="1"
      placeholder="__"
      type="tel"
      onChange={onOtpChangeHandler}
      onKeyUp={(event) => onKeyUpHandler(event.keyCode, index - 1, event.target, index + 1)}
      onPaste={handlePaste}
    />
  ));

  return (
    <FormContainer>
      {step === 1 ? (
        <EmbeddedFormWrapper>
          <FormRow>
            <FormGroupHalf>
              <FormLabel>First Name</FormLabel>
              <EmbeddedInput
                name="firstName"
                placeholder="First name"
                type="text"
                value={formData.firstName}
                onChange={onChangeHandler}
              />
            </FormGroupHalf>

            <FormGroupHalf>
              <FormLabel>Last Name</FormLabel>
              <EmbeddedInput
                name="lastName"
                placeholder="Last name"
                type="text"
                value={formData.lastName}
                onChange={onChangeHandler}
              />
            </FormGroupHalf>
          </FormRow>

          <FormGroup>
            <FormLabel>Email ID</FormLabel>
            <EmbeddedInput
              id="email"
              name="email"
              placeholder="Your email ID"
              type="text"
              value={formData.email}
              onChange={onChangeHandler}
            />
            {errorText.email && <ErrorText>{errorText.email}</ErrorText>}
          </FormGroup>

          <FormGroup>
            <FormLabel>Mobile Number</FormLabel>
            <PhoneNumberInput>
              <EmbeddedDropDownWrapper onClick={() => setShowDropdown(!showDropdown)}>
                <SelectedItem className="active">{selectedCountryCode.code}</SelectedItem>
                <CountryDropdown
                  selectedItem={selectedCountryCode.code}
                  setShowDropdown={setShowDropdown}
                  showDropdown={showDropdown}
                  callback={handleCountryCodeChange}
                />
                <IconDd src={downArrow} alt="Dropdown arrow" key={showDropdown} $showDropdown={showDropdown} />
              </EmbeddedDropDownWrapper>
              <EmbeddedPhoneInput
                id="phoneNumber"
                name="phoneNumber"
                placeholder="Enter phone number"
                type="number"
                value={formData.phoneNumber}
                onChange={onChangeHandler}
              />
            </PhoneNumberInput>
            {errorText.phoneNumber && <ErrorText>{errorText.phoneNumber}</ErrorText>}
          </FormGroup>

          <ReferralCodeContainer>
            <ReferralCheckbox
              isChecked={hasReferralCode}
              role="presentation"
              onClick={handleReferralCheckbox}
            >
              I have a referral code
              <InfoIcon>
                <img src={infoIcon} alt="info" />
                <div className="tooltip">
                  Referral codes help us track who referred you.
                  Using a referral code has no impact on your
                  earnings or commissions.
                </div>
              </InfoIcon>
            </ReferralCheckbox>
            {hasReferralCode && (
              <ReferralInputContainer>
                <InputWrapper>
                  <EmbeddedInput
                    name="referralCode"
                    placeholder="Enter referral code"
                    type="text"
                    value={formData.referralCode || ""}
                    onChange={onChangeHandler}
                  />
                  <ValidateButton
                    onClick={handleReferralValidation}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <Spinner color="#6725f4" size="20px" />
                    ) : (
                      "APPLY"
                    )}
                  </ValidateButton>
                </InputWrapper>
                {getReferralErrorMessage() && (
                  <ErrorText isSuccess={referralStatus === "valid"}>
                    {getReferralErrorMessage()}
                  </ErrorText>
                )}
              </ReferralInputContainer>
            )}
          </ReferralCodeContainer>

          {errorText.captcha && errorText.captcha.length ? (
            <ErrorTextContainer>
              <ErrorText>{errorText.captcha}</ErrorText>
            </ErrorTextContainer>
          ) : null}

          <SubmitButton
            type="submit"
            onClick={submitForm}
          >
            {isLoading ? (
              <Spinner color="#fff" size="20px" />
            ) : (
              "Join as Partner"
            )}
          </SubmitButton>

          <EmbeddedPolicyText>
            By joining, you agree to Wealthy's <SafeLink href={partnersPrivacyUrl} target="_blank" rel="noopener noreferrer">Privacy Policy</SafeLink> and <SafeLink href={partnersPrivacyUrl} target="_blank" rel="noopener noreferrer">Terms of Service</SafeLink>.
          </EmbeddedPolicyText>
        </EmbeddedFormWrapper>
      ) : (
        <OtpWrapper>
          <OtpTitle>Verify your mobile number</OtpTitle>
          <OtpSubtitle>
            {isPartnerExisting ? (
              <WelcomeText>
                This number {selectedCountryCode.code} {formData.phoneNumber} is already linked to an existing account. Please enter the OTP sent to your number to login.
              </WelcomeText>
            ) : (
              `Enter the OTP sent to ${selectedCountryCode.code} ${formData.phoneNumber} to verify your number`
            )}
          </OtpSubtitle>

          <OtpInputsContainer>
            {isLoading ? (
              <Spinner color="#6725f4" size="20px" />
            ) : (
              otpContent
            )}
          </OtpInputsContainer>

          <ResendOtpContainer>
            <span>Didn't receive the OTP? </span>
            {counter === 0 ? (
              <ResendBtn onClick={resendOtp}>Resend</ResendBtn>
            ) : (
              <ResendTimer>You can resend it in: {counter} sec</ResendTimer>
            )}
          </ResendOtpContainer>
        </OtpWrapper>
      )}
    </FormContainer>
  );
};

// ─── Styled Components ───────────────────────────────────────────────
const FormContainer = styled.div`
  width: 100%;
  box-sizing: border-box;
`;

const EmbeddedFormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  font-family: MavenPro, sans-serif;
`;

const FormRow = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 0.5rem;
  box-sizing: border-box;
`;

const FormGroupHalf = styled(FormGroup)`
  width: 48%;
`;

const FormLabel = styled.label`
  font-size: 1.1rem;
  font-weight: 600;
  color: #4f4f4f;
  margin-bottom: 0.6rem;
  text-align: left;
`;

const EmbeddedInput = styled.input`
  background-color: #fff;
  border-radius: 1rem;
  border: 1px solid #d3d3d3;
  padding: 0 1.6rem;
  font-size: 1.1rem;
  height: 3.8rem;
  outline: none;
  box-sizing: border-box;
  width: 100%;
  transition: border-color 0.2s;
  &:focus {
    border-color: #6725f4;
  }
  &::placeholder {
    color: #b0b0b0;
  }
`;

const EmbeddedFieldInfo = styled.span`
  font-size: 1.2rem;
  color: #888;
  margin-top: 0.4rem;
  text-align: left;
`;

const PhoneNumberInput = styled.div`
  display: flex;
  width: 100%;
  box-sizing: border-box;
  position: relative;
`;

const SelectedItem = styled.span`
  font-size: 1.1rem;
  color: #1c1c1c;
`;

const IconDd = styled.img`
  width: 1rem;
  margin-left: 0.4rem;
  transition: transform 0.2s;
  transform: ${props => props.$showDropdown ? "rotate(180deg)" : "rotate(0)"};
`;

const EmbeddedDropDownWrapper = styled.div`
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 1.6rem;
  padding: 0 1.2rem;
  border: 1px solid #d3d3d3;
  border-radius: 1rem 0 0 1rem;
  height: 3.8rem;
  border-right: none;
  cursor: pointer;
  box-sizing: border-box;
  gap: 0.5rem;
`;

const EmbeddedPhoneInput = styled.input`
  background-color: #fff;
  border-radius: 0 1rem 1rem 0;
  border: 1px solid #d3d3d3;
  padding: 0 1.6rem;
  font-size: 1.1rem;
  height: 3.8rem;
  outline: none;
  box-sizing: border-box;
  width: calc(100% - 7rem);
  flex-grow: 1;
  transition: border-color 0.2s;
  &:focus {
    border-color: #6725f4;
  }
  &::placeholder {
    color: #b0b0b0;
  }
`;

const ErrorText = styled.span`
  color: ${props => props.isSuccess ? "#4CAF50" : "red"};
  font-size: 1.3rem;
  margin-top: 0.4rem;
  text-align: left;
`;

const ErrorTextContainer = styled.div`
  display: flex;
  justify-content: center;
  align-content: center;
  margin-top: 0.5rem;
`;

const ReferralCodeContainer = styled.div`
  width: 100%;
  margin-top: 1rem;
`;

const ReferralCheckbox = styled.div`
  cursor: pointer;
  position: relative;
  padding-left: 2rem;
  font-family: MavenPro;
  font-size: 1.5rem;
  font-weight: 500;
  color: #4f4f4f;
  display: flex;
  align-items: center;
  text-align: left;

  &:before, &:after {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    margin: auto;
    border: 1px solid #6725F4;
    border-radius: 2px;
    height: 1.2rem;
    width: 1.2rem;
  }

  ${props => props.isChecked && `
    &:after {
      background-image: url("https://i.wlycdn.com/wealthy-home-page/complete-stage-tick-mark.svg");
      background-size: cover;
    }
  `}
`;

const InfoIcon = styled.div`
  margin-left: 0.6rem;
  position: relative;
  display: inline-flex;
  cursor: pointer;

  img {
    width: 1.4rem;
  }

  .tooltip {
    display: none;
    position: absolute;
    bottom: 2.5rem;
    left: 50%;
    transform: translateX(-50%);
    background-color: #333;
    color: #fff;
    padding: 0.8rem;
    border-radius: 4px;
    font-size: 1.1rem;
    width: 20rem;
    z-index: 10;
    line-height: 1.4;
    text-align: center;
  }

  &:hover .tooltip {
    display: block;
  }
`;

const ReferralInputContainer = styled.div`
  margin-top: 1rem;
  width: 100%;
`;

const InputWrapper = styled.div`
  display: flex;
  width: 100%;
  gap: 1rem;
`;

const ValidateButton = styled.button`
  border-radius: 1rem;
  background-color: #6725f4;
  color: #fff;
  border: none;
  font-size: 1.3rem;
  font-weight: 600;
  height: 3.8rem;
  padding: 0 1.6rem;
  cursor: pointer;
  &:hover {
    opacity: 0.9;
  }
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  height: 3.8rem;
  border-radius: 1.4rem;
  background-color: #6725f4;
  color: #fff;
  border: none;
  font-size: 1.6rem;
  font-weight: 700;
  margin-top: 1rem;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  &:hover {
    opacity: 0.9;
  }
`;

const EmbeddedPolicyText = styled.p`
  text-align: center;
  font-size: 1.2rem;
  color: #777;
  margin-top: 1.6rem;
  line-height: 1.5;
  a {
    color: #6725f4;
    text-decoration: underline;
    font-size: 1.2rem;
  }
`;

// ─── Otp Step Styling ────────────────────────────────────────────────
const OtpWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  font-family: MavenPro, sans-serif;
`;

const OtpTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  color: #1c1c1c;
  margin-bottom: 0.6rem;
`;

const OtpSubtitle = styled.div`
  font-size: 1.3rem;
  color: #666;
  text-align: center;
  line-height: 1.4;
  margin-bottom: 1.6rem;
`;

const WelcomeText = styled.div`
  line-height: 1.4;
`;

const OtpInputsContainer = styled.div`
  display: flex;
  justify-content: space-around;
  width: 100%;
  max-width: 300px;
  margin: 1.5rem 0;
`;

const OtpInput = styled.input`
  text-align: center;
  font-size: 1.6rem;
  border: 1px solid #d3d3d3;
  width: 4.4rem;
  height: 4.4rem;
  border-radius: 8px;
  background-color: #fff;
  outline: none;
  &:focus {
    border-color: #6725f4;
  }
`;

const ResendOtpContainer = styled.div`
  display: flex;
  align-items: center;
  font-size: 1.3rem;
  color: #666;
  margin-top: 1.2rem;
`;

const ResendBtn = styled.button`
  background: none;
  border: none;
  color: #6725f4;
  font-weight: 600;
  cursor: pointer;
  font-size: 1.3rem;
  margin-left: 0.4rem;
  &:hover {
    text-decoration: underline;
  }
`;

const ResendTimer = styled.span`
  margin-left: 0.4rem;
  font-weight: 500;
  color: #555;
`;

export default BlogForm;
